import classNames from "classnames";
import { LocalDate } from "@js-joda/core";
import * as React from "react";
import { allLanguages } from "../language";
import { Geo } from "../messages";
import { STRINGS } from "../strings";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { translate } from "../translate";
import AddressDialog from "./Auth/components/AddressDialog";
import { openMultipleSelectBottomSheet } from "./Generic/AppMultipleSelectBottomSheet";
import Flex from "./Generic/Flex";
import { InfoCard } from "./Generic/InfoCard";
import { ScreenSkeleton } from "./ScreenSkeleton";
import { TextInput } from "./TextInput";

interface Address {
  address: string;
  geo: Geo;
}

export interface ProfileScreenProps {
  caregiver: AppCaregiver;
  isAddressDialogOpen: boolean;
  onBackClick: () => void;
  onProfileUpload: () => void;
  onAddressChange: (address: string, geo: Geo) => void;
  onBirthdayChange: (birthday: LocalDate) => void;
  onLanguagesChange: (languages: string[]) => void;
  onFirstnameChange: (firstName: string) => void;
  onMiddleNameChange: (middleName: string) => void;
  onLastNameChange: (lastName: string) => void;
  onHomePhoneChange: (homePhone: string) => void;
  onAddressInputClick: () => void;
  onRequestCloseAddressDialog: () => void;
}

export class ProfileScreen extends React.PureComponent<ProfileScreenProps, {}> {
  constructor(props: ProfileScreenProps) {
    super(props);

    this.state = {};
  }

  handleAddressChange = (address: Address) => {
    this.props.onAddressChange(address.address, address.geo);
  };

  handleBirthdayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.trim().length !== 0) {
      // HTML date inputs always have a value formatted as "yyyy-mm-dd"
      // <https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/date>
      const birthday = LocalDate.parse(value.trim());
      this.props.onBirthdayChange(birthday);
    }
  };

  handleLanguagesChange = (languages: string[]) => {
    this.props.onLanguagesChange(languages);
  };

  renderCertificationsCell = () => {
    if (this.props.caregiver.agenciesCaregiverCertifications.length === 0) {
      return (
        <div className="ProfileScreen__certificationsNotFound">{translate(STRINGS.NOT_FOUND)}</div>
      );
    }

    return (
      <Flex direction="column" yGap={16}>
        {this.props.caregiver.agenciesCaregiverCertifications.map(
          (agencyCaregiverCertifications, index) => (
            <InfoCard key={index} variant="soft" color="black">
              <div className="ProfileScreen__agencyCaregiverCertifications">
                <div>{agencyCaregiverCertifications.agencyName}</div>
                <div className="__certifications">
                  {agencyCaregiverCertifications.certifications.join(", ")}
                </div>
              </div>
            </InfoCard>
          )
        )}
      </Flex>
    );
  };

  handleClickPlace = (place: google.maps.places.PlaceResult) => {
    if (place.formatted_address !== undefined && place.geometry !== undefined) {
      this.props.onAddressChange(place.formatted_address, {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      });
      this.props.onRequestCloseAddressDialog();
    }
  };

  render() {
    const caregiver = this.props.caregiver;

    return (
      <ScreenSkeleton
        title={translate(STRINGS.PROFILE)}
        titleBarStyle={ScreenSkeleton.TitleBarStyle.PRIMARY}
        onBackClick={() => this.props.onBackClick()}
        rootClassName="ProfileScreen__root"
      >
        <div className="ProfileScreen__header">
          <div className="ProfileScreen__profileCont" onClick={() => this.props.onProfileUpload()}>
            <img
              className={classNames({
                ProfileScreen__profileImg: true,
                Sidebar__blankProfileBackground: caregiver.photoUrl === null,
              })}
              src={caregiver.photoUrl !== null ? caregiver.photoUrl : "assets/default_profile.png"}
            />
          </div>
          <div className="ProfileScreen__subHeader">
            <div className="ProfileScreen__name">
              {caregiver.firstName} {caregiver.middleName} {caregiver.lastName}
            </div>
          </div>
        </div>
        <div className="ProfileScreen__body">
          <ProfileField
            title={translate(STRINGS.FIRST_NAME)}
            cell={
              <TextInput
                className="ProfileScreen__cellTextInput ProfileScreen__cellMod"
                title={translate(STRINGS.FIRST_NAME)}
                value={this.props.caregiver.firstName}
                onChange={this.props.onFirstnameChange}
              />
            }
            editIcon={true}
          />
          <ProfileField
            title={translate(STRINGS.MIDDLE_NAME)}
            cell={
              <TextInput
                className="ProfileScreen__cellTextInput ProfileScreen__cellMod"
                title={translate(STRINGS.MIDDLE_NAME)}
                value={
                  this.props.caregiver.middleName !== null ? this.props.caregiver.middleName : ""
                }
                onChange={this.props.onMiddleNameChange}
              />
            }
            editIcon={true}
          />
          <ProfileField
            title={translate(STRINGS.LAST_NAME)}
            cell={
              <TextInput
                className="ProfileScreen__cellTextInput ProfileScreen__cellMod"
                title={translate(STRINGS.LAST_NAME)}
                value={this.props.caregiver.lastName}
                onChange={this.props.onLastNameChange}
              />
            }
            editIcon={true}
          />
          {/* <ProfileField
                        title={translate(STRINGS.ADDRESS)}
                        cell={<AddressInput
                            className="ProfileScreen__cellMod"
                            value={this.props.caregiver.address}
                            onChange={this.handleAddressChange}
                            isTransparent={false}
                        />}
                        editIcon={true}
                    /> */}
          <ProfileField
            title={translate(STRINGS.ADDRESS)}
            cell={
              <>
                <div className="ProfileScreen__cellMod" onClick={this.props.onAddressInputClick}>
                  {this.props.caregiver.address}
                </div>
                <AddressDialog
                  isOpen={this.props.isAddressDialogOpen}
                  onClickPlace={this.handleClickPlace}
                  onRequestClose={this.props.onRequestCloseAddressDialog}
                  defaultValue={this.props.caregiver.address}
                />
              </>
            }
            editIcon={true}
          />
          {/*{caregiver.phoneNumbers.map((phone, key) =>*/}
          {/*<ProfileField*/}
          {/*key={phone.phoneNumber + key}*/}
          {/*title={translate(STRINGS.HOME_PHONE)}*/}
          {/*cell={<TextInput*/}
          {/*className="ProfileScreen__cellTextInput ProfileScreen__cellMod"*/}
          {/*title={translate(STRINGS.HOME_PHONE)}*/}
          {/*value={phone.phoneNumber}*/}
          {/*onChange={this.props.onHomePhoneChange}*/}
          {/*/>}*/}
          {/*editIcon={true}*/}
          {/*/>)*/}
          {/*}*/}
          <ProfileField
            title={translate(STRINGS.BIRTHDAY)}
            cell={
              <input
                className="ProfileScreen__birthdayCell"
                type="date"
                value={
                  this.props.caregiver.birthDateDate !== null
                    ? this.props.caregiver.birthDateDate.toString()
                    : undefined
                }
                onChange={this.handleBirthdayChange}
              />
            }
            editIcon={true}
          />
          <ProfileField
            title={translate(STRINGS.LANGUAGES)}
            cell={
              <div
                className="ProfileScreen__languagesContainer"
                onClick={() =>
                  openMultipleSelectBottomSheet(allLanguages, this.props.caregiver.languages, {
                    type: "string",
                    onChange: this.handleLanguagesChange,
                  })
                }
              >
                {this.props.caregiver.languages.map((curr) => {
                  return (
                    <span className="ProfileScreen__cellModNoEdit" key={curr}>
                      {curr}
                    </span>
                  );
                })}
              </div>
            }
            editIcon={true}
          />
          <ProfileField
            title={translate(STRINGS.CERTIFICATIONS)}
            cell={this.renderCertificationsCell()}
            editIcon={false}
          />
        </div>
      </ScreenSkeleton>
    );
  }
}

interface ProfileFieldProps {
  title: string;
  cell: JSX.Element;
  editIcon?: boolean;
}

interface ProfileFieldState {}

export class ProfileField extends React.Component<ProfileFieldProps, ProfileFieldState> {
  render() {
    return (
      <div className="ProfileScreen__ProfileField__root">
        <div className="ProfileScreen__ProfileField__title">{this.props.title}:</div>
        <div className="ProfileScreen__ProfileField__value">
          {this.props.editIcon === true ? (
            <div className="ProfileScreen__ProfileField__editIcon" />
          ) : (
            ""
          )}
          {this.props.cell}
        </div>
      </div>
    );
  }
}
